// tslint:disable: max-line-length
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Block from 'lqa-ui/lib/Components/Block';
import ControlSelect from 'lqa-ui/lib/Components/ControlSelect';
import BrandHotelSelector from 'lqa-ui/lib/Applications/Admin/Components/BrandHotelSelector';
import Icon from 'lqa-ui/lib/Components/Icon';
import '../../Dashboard/styles.scss';
import { getItem, removeItem, setItem } from '../../../utils/localStorage';
import '../../../style/main.scss';
import { DateFormatEnum, formatDate } from '../../../helpers/dates';
import { SetDefaultTemplateId } from './SetDefaultTemplateId';
import { AuthMeRes } from '../NotificationBell/AuthMeRes';
import { getBrandAndHotelData } from '../../../helpers/handleBrandAndHotelData';
import { assessments as assessmentApi } from '../../Dashboard/apiCaller';
import { Wrapper } from './Header.styled';
import { IHotelData } from '../../../app/@types/bottomSheet';
import { AssessmentDatePicker } from './components/AssessmentDatePicker';
import { HotelSelectorPortal } from './components/HotelSelectorPortal';
import { CompsetSelectorPortal } from './components/CompsetSelectorPortal';

interface IAssessment {
  stayStart: string;
  id: number;
  score?: number;
  templateId?: number;
}

interface ICompset {
  compSetName: string;
  compSetNumber: number;
}

interface IHotelAssessmentSelection {
  assessments: IAssessment[];
  setAssessments: (assessments: IAssessment[]) => void;
  selectedHotel: string;
  setSelectedHotel: (hotelId: string) => void;
  selectedClient: string;
  setSelectedClient: (clientId: string) => void;
  selectedAssessment?: string;
  setSelectedAssessment: (assessment: string | undefined) => void;
  isSelectDate?: boolean;
  isLoading?: boolean;
  setIsLoading: (loading: boolean) => void;
  lqaCompset?: ICompset[];
  selectedCompset: number;
  setSelectedCompset: (compset: number) => void;
  hasAssessment?: boolean;
  setHasAssessment: (hasAssessment: boolean) => void;
}

function HotelAssessmentSelection ({
  selectedHotel,
  setSelectedHotel,
  selectedClient,
  setSelectedClient,
  selectedAssessment,
  setSelectedAssessment,
  assessments,
  isSelectDate,
  isLoading,
  lqaCompset,
  selectedCompset,
  setSelectedCompset,
  hasAssessment,
  setHasAssessment,
}: IHotelAssessmentSelection) {
  const { t } = useTranslation();
  const userData = getItem('user');
  const allHotels: IHotelData[] = getBrandAndHotelData() || [];
  const initialSelectedBrand: string = getItem('selectedBrand') || '';

  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [isHotelSelectorOpen, setIsHotelSelectorOpen] = useState<boolean>(false);
  const [isCompsetSelectorOpen, setIsCompsetSelectorOpen] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<string>(initialSelectedBrand);

  const isViewer = userData && userData.user && userData.user.isViewer;

  const currentHotel = useMemo<IHotelData | undefined>(
    () =>
      allHotels.find(
        (hotel) =>
          hotel.id === selectedHotel &&
          hotel.clientId === selectedClient &&
          hotel.brandId === selectedBrand
      ),
    [allHotels, selectedHotel, selectedClient, selectedBrand]
  );

  const sortedHotels = useMemo<IHotelData[]>(() => {
    if (!currentHotel) {
      return allHotels;
    }

    const currentName = `${currentHotel.brandName}-${currentHotel.name}`;
    const before = allHotels.filter(
      (hotel) => `${hotel.brandName}-${hotel.name}` < currentName
    );
    const after = allHotels.filter(
      (hotel) => `${hotel.brandName}-${hotel.name}` > currentName
    );

    return [currentHotel, ...after, ...before];
  }, [allHotels, currentHotel]);

  const handleDateSelection = useCallback(
    (dateSelection: { value: string }): void => {
      setSelectedAssessment(dateSelection.value);

      const assessmentData = isSelectDate
        ? assessments.find((assessment) => assessment.stayStart === dateSelection.value)
        : assessments.find((assessment) => assessment.id.toString() === dateSelection.value);

      if (assessmentData) {
        setItem(
          'selectedAssessmentDate',
          isSelectDate ? dateSelection.value : assessmentData.stayStart
        );
        setItem(
          'selectedAssessmentId',
          isSelectDate ? assessmentData.id : dateSelection.value
        );
        setItem('templateId', assessmentData.templateId);
      }
    },
    [assessments, isSelectDate, setSelectedAssessment]
  );

  const handleHotelSelectorClick = useCallback((): void => {
    if (allHotels.length > 1 && !isViewer && !isLoading) {
      setIsHotelSelectorOpen(true);
    }
  }, [allHotels.length, isViewer, isLoading]);

  const handleDateSelectorClick = useCallback((): void => {
    if (!isViewer && !isLoading) {
      setIsDatePickerOpen(true);
    }
  }, [isViewer, isLoading]);

  const handleCompsetSelectorClick = useCallback((): void => {
    if (!isViewer && !isLoading) {
      setIsCompsetSelectorOpen(true);
    }
  }, [isViewer, isLoading]);

  const handleDatePickerClose = useCallback((): void => {
    setIsDatePickerOpen(false);
  }, []);

  const handleCompsetSelectorClose = useCallback((): void => {
    setIsCompsetSelectorOpen(false);
  }, []);

  useEffect(() => {
    if (!selectedHotel || !selectedBrand) {
      return;
    }

    const hotelDetails = allHotels.find(
      (hotel) =>
        hotel.id === selectedHotel &&
        hotel.clientId === selectedClient &&
        hotel.brandId === selectedBrand
    );

    if (!hotelDetails) {
      return;
    }

    // Close the selector immediately
    setIsHotelSelectorOpen(false);

    // Defer heavy operations to allow bottom sheet animation to complete
    const timeoutId = setTimeout(() => {
      setItem('selectedBrand', hotelDetails.brandId);
      setItem('selectedClient', hotelDetails.clientId);

      // Run API calls in parallel for better performance
      Promise.all([
        assessmentApi().then((response) => {
          const hasAssessments = response.length > 0;
          setHasAssessment(hasAssessments);
          setItem('hasAssessment', hasAssessments);

          if (!hasAssessments) {
            removeItem('selectedAssessmentId');
            setSelectedAssessment(undefined);
          }
        }).catch((error) => {
          console.error('Error fetching assessment:', error);
        }),
        AuthMeRes(),
        SetDefaultTemplateId(selectedHotel),
      ]);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedHotel, selectedClient, selectedBrand]);

  return (
    <Wrapper>
      <hr className="thin-x-line" />

      <AssessmentDatePicker
        isOpen={isDatePickerOpen}
        onClose={handleDatePickerClose}
        assessments={assessments}
        selectedAssessment={selectedAssessment}
        isSelectDate={isSelectDate}
        onDateSelect={handleDateSelection}
      />

      {allHotels.length > 1 && (
        <HotelSelectorPortal
          isOpen={isHotelSelectorOpen}
          setIsOpen={setIsHotelSelectorOpen}
          sortedHotels={sortedHotels}
          selectedHotel={selectedHotel}
          setSelectedHotel={setSelectedHotel}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
        />
      )}

      {lqaCompset && lqaCompset.length > 1 && (
        <CompsetSelectorPortal
          isOpen={isCompsetSelectorOpen}
          onClose={handleCompsetSelectorClose}
          selectedHotel={selectedHotel}
          lqaCompset={lqaCompset}
          setSelectedCompset={setSelectedCompset}
          setCompsetOpen={setIsCompsetSelectorOpen}
        />
      )}

      <Block className="header2">
        <BrandHotelSelector>
          <div className="d-flex w-100 brand-hotel-selector">
            <div className="w-54 pr-22" onClick={handleHotelSelectorClick}>
              <div className="color-white f-14">
                {(currentHotel && currentHotel.brandName) || ''}
              </div>
              <ControlSelect
                disabled={true}
                disableSearch={true}
                layout="menu"
                className={`hotel-selector-primary ${allHotels.length > 1 ? '' : 'hotel-selection-dropdown-none'
                  }`}
                value={selectedHotel || '1'}
              >
                <option />
                {allHotels.map((hotel) => (
                  <option
                    key={`${hotel.id}-${hotel.brandId}-${hotel.clientId}`}
                    value={hotel.id}
                  >
                    {hotel.name}
                  </option>
                ))}
              </ControlSelect>
            </div>
            {hasAssessment && (
              <div className="w-46 color-white">
                <div className="f-14">Select Date of Assessment</div>
                <div
                  className="calender-selection d-flex"
                  onClick={handleDateSelectorClick}
                >
                  <Icon
                    className="calender-icon-select pr-5"
                    kind="calendar-icon"
                    height={20}
                    width={20}
                  />{' '}
                  <ControlSelect
                    disabled={true}
                    disableSearch={true}
                    layout="menu"
                    value={selectedAssessment || ''}
                  >
                    <option />
                    {assessments.map((assessment) => (
                      <option
                        key={assessment.id}
                        value={
                          isSelectDate
                            ? assessment.stayStart.toString()
                            : assessment.id
                        }
                      >
                        {formatDate(
                          assessment.stayStart,
                          DateFormatEnum.charDateFormat
                        )}
                      </option>
                    ))}
                  </ControlSelect>
                </div>
              </div>
            )}
          </div>
          {hasAssessment && lqaCompset && (
            <div>
              <div className="w-100 pt-5 pb-5 color-white">
                <div className="f-14" onClick={handleCompsetSelectorClick}>
                  <ControlSelect
                    disabled={true}
                    className={`compset-font primary custom-white-control-select ${lqaCompset.length > 1
                      ? ''
                      : 'hotel-selection-dropdown-none'
                      }`}
                    disableSearch={true}
                    layout="menu"
                    value={selectedCompset ? selectedCompset.toString() : '0'}
                  >
                    <option />
                    {lqaCompset.map((compSet) => (
                      <option
                        key={compSet.compSetNumber}
                        value={compSet.compSetNumber.toString()}
                      >
                        {compSet.compSetName}
                      </option>
                    ))}
                  </ControlSelect>
                </div>
              </div>
            </div>
          )}
        </BrandHotelSelector>
      </Block>
      <hr className="marginBottom20" />
    </Wrapper>
  );
}

export default HotelAssessmentSelection;
